// Der Stand des Medien-Moduls (internal/medien, docs/medien.md).
// GET /v2-api/medien ist Boot-Hydration und Abgleich; jede Änderung der
// Bibliothek kommt über den WS-Kanal "medien" (ganze Bibliothek), jeder
// neue Wiedergabestand über "medien-stand" (ein Stand + die Liste
// „Weiterschauen“). Die Seiten lesen nur diesen Store (docs/data-loading.md).
//
// Läuft NICHT seit dem Boot, sondern ab der ersten Medien-Seite
// (startMedien() oben in der Seite, wie der Kalender): die Bibliothek ist
// etwa 550 KB groß und wird deshalb auch nicht in localStorage gehalten.
// Die Beschreibungen stehen nicht darin; das Detail holt sie (ladeText).

import { SvelteMap } from "svelte/reactivity";

import { csrfHeader, getJSON, postJSON } from "../../lib/api";
import { t } from "../../lib/i18n.svelte";
import { registerRevalidator } from "../../lib/revalidate.svelte";
import { subscribe as wsSubscribe } from "../../lib/ws-hub.svelte";
import type { Abspielen, Art, Bibliothek, Eingang, OrdnerAntwort, Plan, PlanAnfrage, StandArt, StandFrame, StandZeile, Technik, Titel, Treffer, Weg, WegZustand, WeiterZeile } from "./typen";

const ABGLEICH_MS = 300_000;

const _bib = new SvelteMap<string, Bibliothek>();
let _started = false;

function uebernimm(b: Bibliothek): void {
  if (!b || typeof b.bereit !== "boolean") return;
  _bib.set("current", b);
}

// ETag der zuletzt geholten Bibliothek: der Abgleich alle 5 min und bei
// jeder Rückkehr kostet ohne Änderung nur ein 304 (internal/medien/http.go).
let _etag = "";

async function refresh(): Promise<void> {
  try {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (_etag && _bib.has("current")) headers["If-None-Match"] = _etag;
    const r = await fetch("/v2-api/medien", { credentials: "include", cache: "no-store", headers });
    if (r.status === 304 || !r.ok) return;
    _etag = r.headers.get("ETag") ?? "";
    uebernimm((await r.json()) as Bibliothek);
  } catch {
    // Neustart oder Netz weg -- der nächste Frame oder Abgleich holt es nach.
  }
}

/** Ein Stand ersetzt den einen in `staende` (oder kommt dazu), „Weiterschauen“
 *  wird ersetzt. Ohne Bibliothek gibt es nichts zu ändern: der Abruf bringt
 *  den Stand ohnehin mit. */
function uebernimmStand(f: StandFrame): void {
  const b = _bib.get("current");
  if (!b || !f?.stand) return;
  const i = b.staende.findIndex((s) => s.art === f.stand.art && s.id === f.stand.id);
  const staende = i < 0 ? [...b.staende, f.stand] : b.staende.map((s, k) => (k === i ? f.stand : s));
  _bib.set("current", { ...b, staende, weiter: f.weiter ?? b.weiter });
}

// ── Der Weg des Videos (docs/medien.md, Schritt 5) ──────────────────
// Unterwegs läuft die Seite über Cloudflare, das Video kommt direkt von
// forge: über IPv6 oder, ohne IPv6, über Tailscale (medien.adwig.me hat
// dafür auch A 10.0.0.40, Safari nimmt, was geht). Ob das von hier aus geht, prüft die Seite vorher -- beim
// Öffnen und jedes Mal, wenn sie wieder in den Vordergrund kommt (das
// Telefon kann inzwischen in einem anderen Netz sein).

const PRUEF_MS = 6_000;
const _weg = $state<{ z: WegZustand }>({ z: { art: "prueft" } });
let _wegLauf = 0;

async function pruefeWeg(): Promise<void> {
  const lauf = ++_wegLauf;
  let w: Weg;
  try {
    w = await getJSON<Weg>("/v2-api/medien/weg");
  } catch {
    // Ohne Antwort bleibt der letzte Stand; zu Beginn: wie zuhause.
    if (_weg.z.art === "prueft") _weg.z = { art: "zuhause" };
    return;
  }
  if (lauf !== _wegLauf) return;
  if (!w.direkt || !w.pruefen) {
    _weg.z = { art: "zuhause" };
    return;
  }
  let ok = false;
  try {
    // no-cors: die Antwort bleibt unlesbar, aber sie kam an -- mehr braucht
    // es nicht. Kein IPv6, Firewall oder falsches Zertifikat → Fehler.
    await fetch(w.pruefen, { mode: "no-cors", cache: "no-store", credentials: "omit", signal: AbortSignal.timeout(PRUEF_MS) });
    ok = true;
  } catch {
    ok = false;
  }
  if (lauf !== _wegLauf) return;
  _weg.z = ok
    ? { art: "direkt" }
    : {
        art: "gesperrt",
        grund: w.ipv6
          ? t("medien.weg.nicht_erreichbar", "forge ist von hier aus nicht direkt erreichbar – das Netz lässt die Verbindung wohl nicht durch. Mit Tailscale geht es.")
          : t("medien.weg.kein_ipv6", "Dieses Netz hat kein IPv6. Schalte Tailscale ein, dann kommt das Video durch den Tunnel von forge."),
      };
}

/** Den Weg noch einmal prüfen (der Knopf neben dem Grund). */
export function pruefeWegNeu(): void {
  _weg.z = { art: "prueft" };
  void pruefeWeg();
}

/** Wie das Video zu diesem Gerät kommt; „gesperrt“ nennt den Grund. */
export function getWeg(): WegZustand {
  return _weg.z;
}

export function startMedien(): void {
  if (_started) return;
  _started = true;
  registerRevalidator(refresh);
  registerRevalidator(() => void pruefeWeg());
  void refresh();
  void pruefeWeg();
  setInterval(refresh, ABGLEICH_MS);
  wsSubscribe("medien", (d) => uebernimm(d as Bibliothek));
  wsSubscribe("medien-stand", (d) => uebernimmStand(d as StandFrame));
  // Eingang: einmal beim Start (liest die Ordner über SSH, darum nicht im
  // Abgleich alle 5 min); danach meldet das Backend jede Änderung -- nach
  // jedem Scan und jedem Einsortieren.
  registerRevalidator(() => void ladeEingang(false));
  void ladeEingang(false);
  wsSubscribe("medien-eingang", (d) => uebernimmEingang(d as Eingang));
}

/** Die Bibliothek; undefined, solange noch nichts da ist. */
export function getMedien(): Bibliothek | undefined {
  return _bib.get("current");
}

// ── Aktionen ─────────────────────────────────────────────────────────

/** Einen Link zum Abspielen holen (HLS-Adresse und Stand). */
export async function abspielen(art: StandArt, id: number): Promise<Abspielen> {
  return await postJSON<Abspielen>("/v2-api/medien/abspielen", { art, id });
}

/** Den Wiedergabestand merken. Mit `keepalive` übersteht die Anfrage das
 *  Schließen der Seite (sendBeacon geht nicht: es kann den CSRF-Header nicht
 *  setzen). Fehler sind still -- der nächste Stand kommt in 15 s. */
export async function merkeStand(art: StandArt, id: number, position: number, dauer: number, keepalive = false): Promise<void> {
  try {
    await fetch("/v2-api/medien/stand", {
      method: "POST",
      credentials: "include",
      keepalive,
      headers: { Accept: "application/json", "Content-Type": "application/json", ...csrfHeader() },
      body: JSON.stringify({ art, id, position, dauer }),
    });
  } catch {
    // s.o.
  }
}

/** Als gesehen bzw. ungesehen markieren; der neue Stand kommt über den Kanal. */
export async function setzeGesehen(art: StandArt, id: number, gesehen: boolean): Promise<StandZeile> {
  const s = await postJSON<StandZeile>("/v2-api/medien/gesehen", { art, id, gesehen });
  // sofort zeigen; „Weiterschauen“ bringt gleich der Kanal
  if (s) uebernimmStand({ stand: s, weiter: _bib.get("current")?.weiter ?? [] });
  return s;
}

/** Einen Titel aus „Weiterschauen“ nehmen; er gilt dann als nicht gesehen
 *  (eine Serie mit allen Folgen). Die Liste gleich ersetzen, die Stände
 *  bringt der Kanal mit der ganzen Bibliothek. */
export async function entferneWeiter(titel: number): Promise<void> {
  const r = await postJSON<{ weiter: WeiterZeile[] }>("/v2-api/medien/weiter/entfernen", { titel });
  const b = _bib.get("current");
  if (b && r?.weiter) _bib.set("current", { ...b, weiter: r.weiter });
}

// Gemerkt je Titel und Abgleich: ein anderer Treffer bringt einen neuen
// Text und einen neuen bild_stand.
const _texte = new Map<string, Promise<string>>();

/** Die Beschreibung eines Titels; das Detail holt sie beim Aufklappen. */
export function ladeText(titel: Pick<Titel, "id" | "bild_stand">): Promise<string> {
  const k = `${titel.id}:${titel.bild_stand ?? ""}`;
  let p = _texte.get(k);
  if (!p) {
    p = getJSON<{ text: string }>(`/v2-api/medien/text/${titel.id}`).then((a) => a?.text ?? "");
    p.catch(() => _texte.delete(k));
    _texte.set(k, p);
  }
  return p;
}

/** Das ⓘ: Datei, Spuren und was beim Abspielen geschieht. Nur auf Klick. */
export async function ladeTechnik(art: StandArt, id: number): Promise<Technik> {
  return await getJSON<Technik>(`/v2-api/medien/technik/${art}/${id}`);
}

/** Jellyfins Vorschlag für einen neuen Titel annehmen (Name, Text, Poster …
 *  kommen von dort) oder so lassen, der Name aus der Datei bleibt. Die neue
 *  Bibliothek kommt über den Kanal. */
export async function entscheideVorschlag(titelID: number, uebernehmen: boolean): Promise<void> {
  await postJSON<{ ok: boolean }>("/v2-api/medien/vorschlag", { titel_id: titelID, uebernehmen });
}

/** „Anderen Treffer suchen“: Jellyfins Fernsuche zu einem Titel. Das Backend
 *  merkt sich die Treffer (eine Stunde), `nimmTreffer` und die Bilder
 *  beziehen sich darauf. */
export async function sucheTreffer(titelID: number, name: string, jahr?: number): Promise<Treffer[]> {
  const q = new URLSearchParams({ titel_id: String(titelID), name });
  if (jahr) q.set("jahr", String(jahr));
  const a = await getJSON<{ treffer: Treffer[] }>(`/v2-api/medien/suche?${q}`);
  return a.treffer ?? [];
}

/** Einen Treffer der letzten Suche nehmen: Jellyfin wendet ihn an und holt
 *  Texte, Poster und Folgen neu (dauert bei Serien einige Sekunden); die neue
 *  Bibliothek kommt über den Kanal. */
export async function nimmTreffer(titelID: number, tr: Pick<Treffer, "nr" | "stand">): Promise<void> {
  await postJSON<{ ok: boolean }>("/v2-api/medien/suche/anwenden", { titel_id: titelID, nr: tr.nr, stand: tr.stand });
}

// ── Hinzufügen (internal/medien/hinzu.go) ────────────────────────────

const _eingang = $state<{ e: Eingang | null; liest: boolean }>({ e: null, liest: false });

function uebernimmEingang(e: Eingang): void {
  if (e && Array.isArray(e.orte)) _eingang.e = e;
}

/** Was in den Eingangs-Ordnern liegt; `frisch` liest sie jetzt neu (sonst
 *  gilt eine Liste bis 2 min). */
export async function ladeEingang(frisch: boolean): Promise<void> {
  _eingang.liest = true;
  try {
    uebernimmEingang(await getJSON<Eingang>(`/v2-api/medien/eingang${frisch ? "?frisch=1" : ""}`));
  } catch {
    // der Kanal bringt den nächsten Stand
  } finally {
    _eingang.liest = false;
  }
}

/** Der Eingang; null, solange noch nichts da ist. */
export function getEingang(): Eingang | null {
  return _eingang.e;
}

/** Liest der Eingang gerade? */
export function eingangLiest(): boolean {
  return _eingang.liest;
}

/** Einen Ordner als Eingang merken bzw. vergessen (Vault-PIN). */
export async function merkeEingang(rechner: string, pfad: string, merken: boolean): Promise<void> {
  uebernimmEingang(await postJSON<Eingang>("/v2-api/medien/eingang", { rechner, pfad, merken }));
}

/** Freie Wahl eines Ordners (Vault-PIN): ohne Rechner die Liste der
 *  Rechner, ohne Pfad die Freigaben des Rechners. */
export async function holeOrdner(rechner?: string, pfad?: string): Promise<OrdnerAntwort> {
  const q = new URLSearchParams();
  if (rechner) q.set("rechner", rechner);
  if (pfad) q.set("pfad", pfad);
  return await getJSON<OrdnerAntwort>(`/v2-api/medien/ordner?${q}`);
}

/** Der Plan zu Tims Angaben (ohne `art`: der Vorschlag). */
export async function plane(a: PlanAnfrage, signal?: AbortSignal): Promise<Plan> {
  return await postJSON<Plan>("/v2-api/medien/hinzu/plan", a, signal);
}

/** Einsortieren; der Lauf erscheint als Aktion "media.apply" (Pille oben,
 *  Leitung in „Hinzufügen“). */
export async function einsortieren(a: PlanAnfrage): Promise<{ lauf: string; label: string }> {
  return await postJSON<{ lauf: string; label: string }>("/v2-api/medien/hinzu", a);
}

/** Jellyfins Fernsuche, bevor es den Titel gibt; die Bilder liegen unter
 *  der Nummer -1 (trefferBild(-1, …)). */
export async function sucheHinzu(art: Art, name: string, jahr?: number): Promise<Treffer[]> {
  const q = new URLSearchParams({ art, name });
  if (jahr) q.set("jahr", String(jahr));
  const a = await getJSON<{ treffer: Treffer[] }>(`/v2-api/medien/hinzu/suche?${q}`);
  return a.treffer ?? [];
}
